/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { safeStorage } from '@/lib/safe-storage';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export const ThemeProvider = ({
  children,
  defaultTheme = 'light',
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = safeStorage.getItem('theme') as Theme | null;
    return savedTheme || defaultTheme;
  });

  useEffect(() => {
    const root = document.documentElement;

    // Remove both classes first
    root.classList.remove('light', 'dark');

    // Add the current theme class
    root.classList.add(theme);

    // Update theme-color meta tag for safe area
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      // Use the background colors from CSS variables (matching app.css)
      const themeColors = {
        light: 'rgb(250, 249, 245)', // --background in light mode (line 118 in app.css)
        dark: 'rgb(38, 38, 36)', // --background in dark mode (line 171 in app.css)
      };
      metaThemeColor.setAttribute('content', themeColors[theme]);
    }

    // Also update the status bar style for iOS
    const metaStatusBar = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    if (!metaStatusBar) {
      const newMeta = document.createElement('meta');
      newMeta.name = 'apple-mobile-web-app-status-bar-style';
      newMeta.content = theme === 'dark' ? 'black-translucent' : 'default';
      document.head.appendChild(newMeta);
    } else {
      metaStatusBar.setAttribute(
        'content',
        theme === 'dark' ? 'black-translucent' : 'default'
      );
    }

    // Save to localStorage
    safeStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const value = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
